// Lightweight client-side CTA redirect logging.
// POSTs to the logCtaEvent server function so events land in cta_events
// (RLS: admin-readable). Always fire-and-forget — analytics must never
// block or break a navigation.

import { logCtaEvent } from "@/lib/cta.functions";

export type CtaContext = {
  token?: string;
  campaign?: string;
  shift?: string; // shift uuid
  postcode?: string;
  radius?: number;
  ref?: string;
  destination?: string; // optional pre-known onboarding destination path
};

export type CtaRedirectEvent = {
  stage: "auth" | "dashboard" | "onboarding";
  from: string;
  to: string;
  reason?: string;
  role?: string | null;
  onboarding_status?: string | null;
  user_id?: string | null;
  context?: CtaContext;
};

export function logCtaRedirect(event: CtaRedirectEvent): void {
  try {
    // Local CustomEvent for any in-page analytics listener.
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new CustomEvent("cta:redirect", { detail: event }));
    }
  } catch {
    // ignore
  }
  // Server-side persistence (no-await, no-throw).
  void logCtaEvent({
    data: {
      stage: event.stage,
      from_path: event.from,
      to_path: event.to,
      reason: event.reason ?? null,
      role: event.role ?? null,
      onboarding_status: event.onboarding_status ?? null,
      token: event.context?.token ?? null,
      campaign: event.context?.campaign ?? null,
      shift_id: event.context?.shift ?? null,
      postcode: event.context?.postcode ?? null,
      radius_km: event.context?.radius ?? null,
      user_id: event.user_id ?? null,
    },
  }).catch(() => {
    // swallow — analytics never breaks a redirect
  });
}

// Pull CTA context fields out of a parsed search-params object.
export function pickCtaContext(search: Record<string, unknown> | undefined | null): CtaContext {
  if (!search) return {};
  const out: CtaContext = {};
  if (typeof search.ct === "string" && search.ct) out.token = search.ct;
  if (typeof search.token === "string" && search.token) out.token = search.token;
  if (typeof search.campaign === "string" && search.campaign) out.campaign = search.campaign;
  if (typeof search.shift === "string" && search.shift) out.shift = search.shift;
  if (typeof search.postcode === "string" && search.postcode) out.postcode = search.postcode;
  if (typeof search.radius === "number" && Number.isFinite(search.radius)) out.radius = search.radius;
  if (typeof search.ref === "string" && search.ref) out.ref = search.ref;
  if (typeof search.destination === "string" && search.destination) out.destination = search.destination;
  return out;
}
